import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Backdrop,
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Slider,
  Stack,
  Tab,
  Tabs,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ViewInArOutlinedIcon from "@mui/icons-material/ViewInArOutlined";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import ZoomInOutlinedIcon from "@mui/icons-material/ZoomInOutlined";
import ZoomOutOutlinedIcon from "@mui/icons-material/ZoomOutOutlined";
import FitScreenOutlinedIcon from "@mui/icons-material/FitScreenOutlined";
import ChevronLeftOutlinedIcon from "@mui/icons-material/ChevronLeftOutlined";
import ChevronRightOutlinedIcon from "@mui/icons-material/ChevronRightOutlined";
import Tooltip from "@mui/material/Tooltip";
import type { Swagpack } from "../../../../../../models/Swagstore/Packs";
import type { PSDTemplate } from "../../../../../../models/MockupConfig/index";
import { useLogoPlacement } from "../../../pages/mockup-generator/useLogoPlacement";
import { useMockupEditorPreviewUrl } from "../../../pages/mockup-generator/useMockupEditorPreviewUrl";
import { useLogoMetadataDrafts } from "../../../pages/mockup-generator/useLogoMetadataDrafts";
import { usePrintingConfig } from "../../../pages/mockup-generator/usePrintingConfig";
import { useMockupGeneratorTabs } from "../../../pages/mockup-generator/useMockupGeneratorTabs";
import { useStores } from "../../../../use-stores";
import { normalizePrintingType } from "../../../../../../utils/printingType";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import AppDropDown from "../../../../../../views/components/common/filter/AppDropDown";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import FormTextField from "../../../../../../views/components/common/form/FormTextField";
import { observer } from "mobx-react";
import {
  GENERATOR_MODAL_MIN_HEIGHT,
  EDITOR_UI,
  editorFieldSx,
  editorIconBtnSx,
  logoCardSx,
  logoDeleteBtnSx,
  printAreaSx,
  logoSelectionSx,
  resizeHandleSx,
} from "./mockupGeneratorEditorStyles";
import {
  sortPsdTemplates,
  pickPreferredMockupSize,
} from "./mockupGeneratorUtils";
import {
  EmptyStatePanel,
  EditorPanelCard,
} from "./MockupGeneratorSharedUI";
import MockupManualUploadTab from "./MockupManualUploadTab";
import MockupExportsTab from "./MockupExportsTab";
import { catalogSkuChoices, catalogSkuForRequest } from "./catalogSkuChoices";

interface MockupGeneratorModalProps {
  open: boolean;
  product: Swagpack | null;
  onClose: () => void;
  variantSku?: string;
  onVariantSkuChange?: (sku: string) => void;
  initialTab?: "generate" | "exports" | "upload";
  placementTypes: { id: number; sort_order?: number }[];
  psdTemplates: PSDTemplate[];
  selectedPsdId: number | null;
  loadingPsd: boolean;
  savedMockups: Array<{
    id: number;
    reference_id: string | null;
    mockup_url: string;
    created_at: string;
  }>;
  mockupOutputs: { large: string; medium: string; small: string } | null;
  mockupReferenceId: string | null;
  generatingMockup: boolean;
  updatingMockupId?: number | null;
  deletingGeneratedMockupId: number | null;
  lockPsdSelection?: boolean;
  editMode?: boolean;
  editMockupId?: number | null;
  hideGeneratedMockupsTab?: boolean;
  onSelectPsdId: (id: number | null) => void;
  onGenerateMockup: (payload: {
    psd_template_id: number;
    logoFile?: File | null;
    logoUrl?: string | null;
    x: number;
    y: number;
    width: number;
    height: number;
    rotation: number;
    template_required?: boolean;
    printing_type?: string;
    /** Informational print size for template details; does not affect logo placement. */
    logo_size?: string;
    /** Informational brand color codes for template details; does not affect logo. */
    rgb_code?: string;
    cmyk_code?: string;
  }) => void | Promise<void>;
  uploadedLogos: Array<{ id: number; logo_url: string }>;
  loadingUploadedLogos: boolean;
  onUploadLogoToLibrary: (file: File) => void;
  onDeleteUploadedLogo: (id: number) => Promise<void>;
  onDeleteMockup: (id: number) => Promise<void>;
  onManualUploadMockup?: (payload: { id: number; file: File }) => Promise<void>;
  onUploadMockupFiles?: (files: File[]) => Promise<void>;
  onError: (title: string, message?: string) => void;
  embedded?: boolean;
  hideInternalTabs?: boolean;
  controlledTab?: "generate" | "upload" | "exports";
}

const MockupGeneratorModal: React.FC<MockupGeneratorModalProps> = ({
  open,
  product,
  onClose,
  variantSku = "__all__",
  onVariantSkuChange,
  initialTab = "generate",
  placementTypes,
  psdTemplates,
  selectedPsdId,
  loadingPsd,
  savedMockups,
  mockupOutputs,
  mockupReferenceId,
  generatingMockup,
  updatingMockupId = null,
  deletingGeneratedMockupId,
  lockPsdSelection = false,
  editMode = false,
  editMockupId = null,
  hideGeneratedMockupsTab = false,
  onSelectPsdId,
  onGenerateMockup,
  uploadedLogos,
  loadingUploadedLogos,
  onUploadLogoToLibrary,
  onDeleteUploadedLogo,
  onDeleteMockup,
  onManualUploadMockup,
  onUploadMockupFiles,
  onError,
  embedded = false,
  hideInternalTabs = false,
  controlledTab,
}) => {
  const logoLibraryInputRef = useRef<HTMLInputElement>(null);
  const [editorZoom, setEditorZoom] = useState(1);
  const [leftSidebarCollapsed, setLeftSidebarCollapsed] = useState(false);

  const skuChoices = useMemo(() => catalogSkuChoices(product), [product]);
  const preferredSize = useMemo(() => pickPreferredMockupSize(), []);
  const sortedPsdTemplates = sortPsdTemplates(psdTemplates, placementTypes);
  const selectedPsdTemplate = sortedPsdTemplates.find((p) => p.id === selectedPsdId);
  const { mockupConfigStore } = useStores();

  const logoPlacement = useLogoPlacement({
    selectedPsdId,
    zoom: editorZoom,
    psdTemplate: selectedPsdTemplate ?? null,
    onError,
  });
  const canvasWidth = logoPlacement.displaySize.width;
  const canvasHeight = logoPlacement.displaySize.height;

  const metadata = useLogoMetadataDrafts({
    open,
    logoPlacement,
    selectedPsdTemplate,
    selectedPsdId,
  });

  const printing = usePrintingConfig({
    open,
    productId: product?.id ? Number(product.id) : undefined,
  });

  const tabs = useMockupGeneratorTabs({
    open,
    productId: product?.id ? Number(product.id) : undefined,
    initialTab,
    controlledTab,
    editMode,
    hideGeneratedMockupsTab,
    embedded,
    generatingMockup,
    savedMockupsCount: savedMockups.length,
    hasMockupOutput: Boolean(mockupOutputs?.[preferredSize]),
  });

  const handleBlankPreviewResolved = useCallback(
    (templateId: number, url: string) => {
      mockupConfigStore.setPsdTemplateBlankPreviewUrl(templateId, url);
    },
    [mockupConfigStore],
  );

  const { previewImageUrl, loadingPlacementPreview } = useMockupEditorPreviewUrl({
    template: selectedPsdTemplate,
    logoUrl: logoPlacement.logoUrl,
    onBlankPreviewResolved: handleBlankPreviewResolved,
  });

  useEffect(() => {
    logoPlacement.handleLogoUrl(null);
    setEditorZoom(1);
    setLeftSidebarCollapsed(false);
  }, [open, product?.id, editMode, initialTab]);

  useEffect(() => {
    if (!logoPlacement.logoUrl) return;
    const stillExists = uploadedLogos.some(
      (logo) => logo.logo_url === logoPlacement.logoUrl,
    );
    if (!stillExists) {
      logoPlacement.handleLogoUrl(null);
    }
  }, [uploadedLogos, logoPlacement.logoUrl, logoPlacement]);

  const hasNoPsds = Boolean(product?.id) && !loadingPsd && psdTemplates.length === 0;
  const skuSelected = catalogSkuForRequest(variantSku ?? "") != null;
  const noPsdMessage = skuSelected
    ? "No PSD for this SKU. A product PSD is not used for another color."
    : "Select a SKU. New mockups are saved against that SKU. Older product PSDs still appear here.";
  const isBusy =
    generatingMockup ||
    Boolean(editMode && editMockupId && updatingMockupId === editMockupId);

  const psdTemplateOptions = useMemo(() => {
    const base = sortedPsdTemplates
      .filter((p) => p.id != null)
      .map((psd) => ({
        id: String(psd.id!),
        value: psd.placement_type_name ?? `Template ${psd.id}`,
      }));
    return [{ id: "", value: "Choose a PSD view..." }, ...base];
  }, [sortedPsdTemplates]);

  const handleManualUploadSave = useCallback(async () => {
    if (!skuSelected) {
      onError(
        "SKU required",
        "Select a SKU. New mockups are saved against one SKU.",
      );
      return;
    }
    if (!tabs.manualUploadFiles?.length) {
      onError("Image required", "Please select an image to upload.");
      return;
    }
    if (editMode && editMockupId) {
      if (!onManualUploadMockup) return;
      await onManualUploadMockup({ id: editMockupId, file: tabs.manualUploadFiles[0] });
    } else if (onUploadMockupFiles) {
      if (!hideGeneratedMockupsTab) {
        tabs.markAutoSwitchToExports();
      }
      await onUploadMockupFiles(tabs.manualUploadFiles);
    } else {
      onError("No mockup", "Missing mockup id for manual upload.");
      return;
    }
    tabs.setManualUploadFiles([]);
  }, [
    editMockupId,
    editMode,
    skuSelected,
    hideGeneratedMockupsTab,
    tabs.manualUploadFiles,
    tabs.setManualUploadFiles,
    tabs.markAutoSwitchToExports,
    onManualUploadMockup,
    onUploadMockupFiles,
  ]);

  const removeManualUploadPreview = useCallback((image: { title?: string }) => {
    tabs.setManualUploadFiles((prev) => {
      if (!prev.length) return prev;
      const index = prev.findIndex((file) => file.name === image?.title);
      if (index < 0) return prev;
      return prev.filter((_, i) => i !== index);
    });
  }, [tabs.setManualUploadFiles]);

  const handleGenerateMockup = useCallback(async () => {
    if (!skuSelected) {
      return onError(
        "SKU required",
        "Select a SKU. New mockups are saved against one SKU.",
      );
    }
    const selectedLogoUrl = logoPlacement.getSelectedLogoUrl();
    if (!selectedLogoUrl) {
      return onError("No logo", "Please upload to library and select a logo first.");
    }
    if (!selectedPsdId || !selectedPsdTemplate) {
      return onError("No template", "Please select a PSD template.");
    }
    const coords = logoPlacement.getCanvasCoords(
      selectedPsdTemplate.canvas_w ?? 800,
      selectedPsdTemplate.canvas_h ?? 600,
    );
    if (!coords) return onError("Error", "Could not compute logo coordinates.");
    if (printing.printingTypes.length > 0 && !printing.selectedPrintingType) {
      return onError("Printing type", "Please select a printing type for this product.");
    }
    if (!hideGeneratedMockupsTab) {
      tabs.markAutoSwitchToExports();
    }
    await onGenerateMockup({
      psd_template_id: selectedPsdId,
      logoUrl: selectedLogoUrl,
      x: coords.x,
      y: coords.y,
      width: coords.width,
      height: coords.height,
      rotation: coords.rotation,
      template_required: printing.templateRequired,
      printing_type:
        normalizePrintingType(printing.selectedPrintingType) ?? undefined,
      logo_size: metadata.getCapturedLogoSizeLabel(),
      rgb_code: metadata.getCapturedColorCode(metadata.rgbCodeDraft),
      cmyk_code: metadata.getCapturedColorCode(metadata.cmykCodeDraft),
    });
  }, [
    hideGeneratedMockupsTab,
    skuSelected,
    printing.templateRequired,
    printing.printingTypes.length,
    printing.selectedPrintingType,
    logoPlacement,
    metadata,
    onGenerateMockup,
    selectedPsdId,
    selectedPsdTemplate,
    tabs.markAutoSwitchToExports,
  ]);

  const handleCopyMockupUrl = useCallback((url: string) => {
    void navigator.clipboard.writeText(url);
  }, []);

  const contentSx = {
    minHeight: embedded
      ? 0
      : tabs.isCompactUploadDialog
        ? undefined
        : 0,
    height: embedded || !tabs.isCompactUploadDialog ? "100%" : undefined,
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column" as const,
    overflow: "hidden",
    pt: tabs.isCompactUploadDialog ? 2 : embedded ? 0 : undefined,
    pb: tabs.isCompactUploadDialog ? 2 : embedded ? 0 : undefined,
    px: tabs.isCompactUploadDialog ? 3 : embedded ? 0.5 : undefined,
  };

  const skuControl =
    !embedded && skuChoices.length > 1 ? (
      <Box sx={{ width: 220, flexShrink: 0, mb: 0.5 }}>
        <AppDropDown
          title="SKU"
          isMultiSelect={false}
          list={skuChoices}
          value={variantSku}
          disabled={editMode && catalogSkuForRequest(variantSku) != null}
          onSingleSelectionChange={(id) => {
            if (editMode && catalogSkuForRequest(variantSku) != null) return;
            onVariantSkuChange?.(id);
          }}
          placeholderText="All SKUs"
          width="220px"
          height="32px"
          fontSize="0.75rem"
        />
      </Box>
    ) : null;

  const modalBody = (
    <>
      {((tabs.showTabs && !hideInternalTabs) || Boolean(skuControl)) && (
        <Box
          sx={{
            mb: 2,
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexShrink: 0,
          }}
        >
          {tabs.showTabs && !hideInternalTabs && (
            <Tabs
              value={tabs.activeTab}
              onChange={(_, value) =>
                tabs.setActiveTab(value as "generate" | "exports" | "upload")
              }
              sx={{
                flex: 1,
                minHeight: 40,
                "& .MuiTab-root": {
                  fontSize: 14,
                  fontWeight: 600,
                  textTransform: "none",
                  minHeight: 40,
                },
                "& .MuiTabs-indicator": {
                  height: 3,
                  borderRadius: "3px 3px 0 0",
                  bgcolor: EDITOR_UI.primary,
                },
              }}
            >
              {tabs.showGenerateTab && <Tab value="generate" label="Generate" />}
              {tabs.showUploadTab && <Tab value="upload" label="Upload" />}
              {tabs.showExportsTab && (
                <Tab value="exports" label="Generated mockups" />
              )}
            </Tabs>
          )}
          {skuControl}
        </Box>
      )}

      {tabs.showGenerateContent && (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <>
            {hasNoPsds && (
              <Box
                sx={{
                  mt: 0,
                  flex: 1,
                  minHeight: embedded ? "100%" : undefined,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <EmptyStatePanel
                  title="No PSD Templates"
                  message={noPsdMessage}
                  minHeight={0}
                />
              </Box>
            )}

            {sortedPsdTemplates?.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flex: 1,
                  minHeight: 0,
                  bgcolor: EDITOR_UI.pageBg,
                  borderRadius: embedded ? 0 : 2,
                  overflow: "hidden",
                  height: "100%",
                }}
              >
                {product?.label ? (
                  <Box
                    sx={{
                      px: { xs: 2, md: 3 },
                      py: 1.5,
                      borderBottom: "1px solid",
                      borderColor: EDITOR_UI.border,
                      bgcolor: "common.white",
                    }}
                  >
                    <CustomTypography
                      variant="heading5"
                      sx={{ fontSize: { xs: 22, md: 28 }, fontWeight: 700, lineHeight: 1.2 }}
                    >
                      {product.label}
                    </CustomTypography>
                    <CustomTypography variant="caption" sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>
                      Position your logo and generate a production-ready mockup
                    </CustomTypography>
                  </Box>
                ) : null}

                {previewImageUrl ? (
                  <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", lg: "row" },
                        flex: 1,
                        minHeight: 0,
                        overflow: "hidden",
                      }}
                    >
                      {/* Left sidebar — collapsible */}
                      <Box
                        sx={{
                          width: {
                            xs: "100%",
                            lg: leftSidebarCollapsed
                              ? EDITOR_UI.leftCollapsedWidth
                              : EDITOR_UI.leftWidth,
                          },
                          flexShrink: 0,
                          borderRight: { lg: "1px solid" },
                          borderColor: EDITOR_UI.border,
                          bgcolor: "common.white",
                          display: "flex",
                          flexDirection: "column",
                          height: { lg: "100%" },
                          minHeight: 0,
                          maxHeight: { xs: "32vh", lg: "none" },
                          overflow: "hidden",
                          transition: "width 0.2s ease",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: leftSidebarCollapsed ? "center" : "space-between",
                            px: leftSidebarCollapsed ? 0.5 : 2,
                            py: 1.25,
                            borderBottom: "1px solid",
                            borderColor: EDITOR_UI.border,
                            flexShrink: 0,
                          }}
                        >
                          {!leftSidebarCollapsed ? (
                            <CustomTypography variant="body2" sx={{ fontSize: 13, fontWeight: 600, color: "text.secondary" }}>
                              Assets
                            </CustomTypography>
                          ) : null}
                          <Tooltip title={leftSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"} arrow>
                            <IconButton
                              size="small"
                              aria-label={leftSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                              onClick={() => setLeftSidebarCollapsed((v) => !v)}
                              sx={editorIconBtnSx}
                            >
                              {leftSidebarCollapsed ? (
                                <ChevronRightOutlinedIcon fontSize="small" />
                              ) : (
                                <ChevronLeftOutlinedIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        </Box>

                        {!leftSidebarCollapsed ? (
                          <Box
                            sx={{
                              flex: 1,
                              minHeight: 0,
                              overflowY: "auto",
                              overflowX: "hidden",
                              p: 2,
                            }}
                          >
                            <EditorPanelCard
                              icon={<ViewInArOutlinedIcon fontSize="small" />}
                              title="Template"
                            >
                              <Stack spacing={2}>
                                <AppDropDown
                                  title="view"
                                  isMultiSelect={false}
                                  list={psdTemplateOptions}
                                  value={selectedPsdId != null ? String(selectedPsdId) : ""}
                                  onSingleSelectionChange={(id) => {
                                    if (lockPsdSelection) return;
                                    if (!id) return;
                                    onSelectPsdId(Number(id));
                                  }}
                                  placeholderText="Choose a view..."
                                  width="100%"
                                  disabled={lockPsdSelection}
                                />
                                {(printing.loadingPrintingTypes || printing.printingTypes.length > 0) && (
                                  <AppDropDown
                                    title="Printing type"
                                    isMultiSelect={false}
                                    inlinePlaceholder
                                    list={printing.printingTypeOptions}
                                    value={printing.selectedPrintingType}
                                    onSingleSelectionChange={(type) => printing.setSelectedPrintingType(type)}
                                    placeholderText={
                                      printing.loadingPrintingTypes ? "Loading..." : "Select printing type..."
                                    }
                                    width="100%"
                                    disabled={generatingMockup || printing.loadingPrintingTypes}
                                    required={printing.printingTypes.length > 0}
                                  />
                                )}
                                <FormControlLabel
                                  sx={{ alignItems: "flex-start", mr: 0, ml: 0 }}
                                  control={
                                    <Checkbox
                                      size="small"
                                      checked={printing.templateRequired}
                                      onChange={(e) => printing.setTemplateRequired(e.target.checked)}
                                      disabled={generatingMockup}
                                      sx={{
                                        pt: 0.25,
                                        "&.Mui-checked": { color: EDITOR_UI.primary },

                                      }}
                                    />
                                  }
                                  label={
                                    <Box>
                                      <CustomTypography variant="body2" sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.3, mt: 0.5 }}>
                                        Include mockup template
                                      </CustomTypography>
                                    </Box>
                                  }
                                />
                              </Stack>
                            </EditorPanelCard>

                            <EditorPanelCard
                              icon={<CollectionsOutlinedIcon fontSize="small" />}
                              title="Logo library"
                            >
                              {logoPlacement.logoUrl ? (
                                <AppButton
                                  label="Clear selection"
                                  variant="secondary"
                                  onClick={() => logoPlacement.handleLogoUrl(null)}
                                  disabled={generatingMockup}
                                  customStyles={{
                                    width: "100%",
                                    borderRadius: 8,
                                    fontSize: 13,
                                    minHeight: 36,
                                    border: `1px solid ${EDITOR_UI.border}`,
                                    marginBottom: "12px",
                                  }}
                                />
                              ) : null}
                              {loadingUploadedLogos ? (
                                <CustomTypography variant="body2" sx={{ fontSize: 14, color: "text.secondary" }}>
                                  Loading logos...
                                </CustomTypography>
                              ) : uploadedLogos.length === 0 ? (
                                <Box
                                  sx={{
                                    py: 4,
                                    px: 2,
                                    borderRadius: 2,
                                    border: "2px dashed",
                                    borderColor: "rgba(79, 70, 229, 0.25)",
                                    bgcolor: "rgba(79, 70, 229, 0.03)",
                                    textAlign: "center",
                                  }}
                                >
                                  <CollectionsOutlinedIcon sx={{ fontSize: 32, color: EDITOR_UI.primaryLight, mb: 1 }} />
                                  <CustomTypography variant="body2" sx={{ fontSize: 14, color: "text.secondary" }}>
                                    Upload a logo to get started
                                  </CustomTypography>
                                </Box>
                              ) : (
                                <Box
                                  sx={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(2, 1fr)",
                                    gap: 1.5,
                                  }}
                                >
                                  {uploadedLogos.map((logo) => {
                                    const isSelected = logoPlacement.logoUrl === logo.logo_url;
                                    return (
                                      <Box
                                        key={logo.id}
                                        className="logo-card"
                                        onClick={() => logoPlacement.handleLogoUrl(logo.logo_url)}
                                        sx={logoCardSx(isSelected)}
                                      >
                                        <Box
                                          component="img"
                                          src={logo.logo_url}
                                          alt="Uploaded logo"
                                          sx={{
                                            maxWidth: "100%",
                                            maxHeight: "100%",
                                            objectFit: "contain",
                                            display: "block",
                                          }}
                                        />
                                        {isSelected ? (
                                          <CheckCircleOutlineIcon
                                            fontSize="small"
                                            sx={{
                                              position: "absolute",
                                              top: 8,
                                              right: 8,
                                              color: EDITOR_UI.primary,
                                              bgcolor: "background.paper",
                                              borderRadius: "50%",
                                            }}
                                          />
                                        ) : null}
                                        <Tooltip title="Delete Logo" arrow>
                                          <IconButton
                                            className="logo-card-delete"
                                            size="small"
                                            aria-label="Delete Logo"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              if (logoPlacement.logoUrl === logo.logo_url) {
                                                logoPlacement.handleLogoUrl(null);
                                              }
                                              void onDeleteUploadedLogo(logo.id);
                                            }}
                                            sx={logoDeleteBtnSx}
                                          >
                                            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    );
                                  })}
                                </Box>
                              )}
                              <AppButton
                                label="Upload logo"
                                startIcon={<CloudUploadIcon />}
                                onClick={() => logoLibraryInputRef.current?.click()}
                                customStyles={{ width: "100%", marginTop: "12px" }}
                              />
                              <input
                                ref={logoLibraryInputRef}
                                type="file"
                                accept=".png,.jpg,.jpeg,.webp,.svg"
                                hidden
                                onChange={(e) => {
                                  const file = e.target.files?.[0] ?? null;
                                  if (file) {
                                    void onUploadLogoToLibrary(file);
                                  }
                                  e.currentTarget.value = "";
                                }}
                              />
                            </EditorPanelCard>

                            <EditorPanelCard
                              key={logoPlacement.logoUrl ? "properties-open" : "properties-closed"}
                              icon={<TuneOutlinedIcon fontSize="small" />}
                              title="Properties"
                              defaultExpanded={!!logoPlacement.logoUrl}
                            >
                              {!logoPlacement.logoUrl ? (
                                <Box sx={{ py: 1.5, textAlign: "center" }}>
                                  <TuneOutlinedIcon sx={{ fontSize: 28, color: "text.disabled", mb: 0.75 }} />
                                  <CustomTypography variant="body2" sx={{ fontSize: 13, color: "text.secondary" }}>
                                    Select a logo to edit its properties
                                  </CustomTypography>
                                </Box>
                              ) : (
                                <Stack spacing={2}>
                                  <Box>
                                    <CustomTypography variant="caption" sx={{ fontSize: 13, fontWeight: 600, mb: 1, color: "text.secondary", display: "block" }}>
                                      Rotation
                                    </CustomTypography>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                      <IconButton
                                        size="small"
                                        aria-label="Decrease rotation by 1 degree"
                                        onClick={() => logoPlacement.rotate(-1)}
                                        disabled={generatingMockup}
                                        sx={editorIconBtnSx}
                                      >
                                        <RemoveIcon fontSize="small" />
                                      </IconButton>
                                      <FormTextField
                                        value={metadata.rotationDraft}
                                        onFocus={() => metadata.setRotationFocused(true)}
                                        onBlur={metadata.commitRotationDraft}
                                        onChange={(_, value) => metadata.setRotationDraft(value ?? "")}
                                        onKeyDown={(e) => {
                                          if (e.key === "Enter") metadata.commitRotationDraft();
                                        }}
                                        disabled={generatingMockup}
                                        endAdornment="°"
                                        marginBottom={0}
                                        borderColor={EDITOR_UI.border}
                                        sx={{
                                          ...editorFieldSx,
                                          width: 88,
                                          "& .MuiInputBase-input": { textAlign: "center" },
                                        }}
                                      />
                                      <IconButton
                                        size="small"
                                        aria-label="Increase rotation by 1 degree"
                                        onClick={() => logoPlacement.rotate(1)}
                                        disabled={generatingMockup}
                                        sx={editorIconBtnSx}
                                      >
                                        <AddIcon fontSize="small" />
                                      </IconButton>
                                    </Stack>
                                  </Box>
                                  <Box>
                                    <CustomTypography variant="caption" sx={{ fontSize: 13, fontWeight: 600, mb: 1, color: "text.secondary", display: "block" }}>
                                      Size (inches)
                                    </CustomTypography>
                                    <Stack direction="row" spacing={1}>
                                      <FormTextField
                                        placeholder="Width"
                                        value={metadata.widthDraft}
                                        onFocus={() => metadata.setSizeFocused("width")}
                                        onBlur={() => metadata.commitCapturedSizeDraft("width")}
                                        onChange={(_, value) => metadata.setWidthDraft(value ?? "")}
                                        onKeyDown={(e) => {
                                          if (e.key === "Enter") metadata.commitCapturedSizeDraft("width");
                                        }}
                                        disabled={generatingMockup}
                                        endAdornment="in"
                                        marginBottom={0}
                                        borderColor={EDITOR_UI.border}
                                        sx={{ ...editorFieldSx, flex: 1 }}
                                      />
                                      <FormTextField
                                        placeholder="Height"
                                        value={metadata.heightDraft}
                                        onFocus={() => metadata.setSizeFocused("height")}
                                        onBlur={() => metadata.commitCapturedSizeDraft("height")}
                                        onChange={(_, value) => metadata.setHeightDraft(value ?? "")}
                                        onKeyDown={(e) => {
                                          if (e.key === "Enter") metadata.commitCapturedSizeDraft("height");
                                        }}
                                        disabled={generatingMockup}
                                        endAdornment="in"
                                        marginBottom={0}
                                        borderColor={EDITOR_UI.border}
                                        sx={{ ...editorFieldSx, flex: 1 }}
                                      />
                                    </Stack>
                                    <CustomTypography
                                      variant="caption"
                                      sx={{ mt: 0.75, color: "text.secondary", display: "block", fontSize: 11 }}
                                    >
                                      For printing details only — resize the logo by dragging on the canvas.
                                    </CustomTypography>
                                  </Box>
                                  <Box>
                                    <CustomTypography variant="caption" sx={{ fontSize: 13, fontWeight: 600, mb: 1, color: "text.secondary", display: "block" }}>
                                      Brand logo colors
                                    </CustomTypography>
                                    <Stack spacing={1.5}>
                                      <FormTextField
                                        title="RGB code"
                                        placeholder="e.g. 255, 87, 51"
                                        value={metadata.rgbCodeDraft}
                                        onChange={(_, value) => metadata.setRgbCodeDraft(value ?? "")}
                                        disabled={generatingMockup}
                                        marginBottom={0}
                                        borderColor={EDITOR_UI.border}
                                        sx={editorFieldSx}
                                      />
                                      <FormTextField
                                        title="CMYK code"
                                        placeholder="e.g. 0, 67, 80, 0"
                                        value={metadata.cmykCodeDraft}
                                        onChange={(_, value) => metadata.setCmykCodeDraft(value ?? "")}
                                        disabled={generatingMockup}
                                        marginBottom={0}
                                        borderColor={EDITOR_UI.border}
                                        sx={editorFieldSx}
                                      />
                                    </Stack>
                                    <CustomTypography
                                      variant="caption"
                                      sx={{ mt: 0.75, color: "text.secondary", display: "block", fontSize: 11 }}
                                    >
                                      For printing details only — does not change the logo.
                                    </CustomTypography>
                                  </Box>
                                </Stack>
                              )}
                            </EditorPanelCard>
                          </Box>
                        ) : (
                          <Stack spacing={1} sx={{ py: 2, alignItems: "center" }}>
                            <Tooltip title="Templates" arrow placement="right">
                              <IconButton size="small" aria-label="Templates" sx={editorIconBtnSx}>
                                <ViewInArOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Logo library" arrow placement="right">
                              <IconButton
                                size="small"
                                aria-label="Logo library"
                                onClick={() => setLeftSidebarCollapsed(false)}
                                sx={editorIconBtnSx}
                              >
                                <CollectionsOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Properties" arrow placement="right">
                              <IconButton
                                size="small"
                                aria-label="Properties"
                                onClick={() => setLeftSidebarCollapsed(false)}
                                sx={editorIconBtnSx}
                              >
                                <TuneOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        )}
                      </Box>

                      {/* Canvas workspace — full remaining width */}
                      <Box
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          minHeight: 0,
                          display: "flex",
                          flexDirection: "column",
                          bgcolor: EDITOR_UI.workspaceBg,
                          backgroundImage: EDITOR_UI.dotGrid,
                          backgroundSize: "20px 20px",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            flex: 1,
                            minHeight: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            p: { xs: 0.5, md: 0.75 },
                            overflow: "auto",
                          }}
                        >
                          <Box
                            sx={{
                              borderRadius: 2.5,
                              boxShadow: "0 16px 40px rgba(15, 23, 42, 0.1), 0 2px 8px rgba(15, 23, 42, 0.05)",
                              overflow: "hidden",
                              bgcolor: "common.white",
                              flexShrink: 0,
                            }}
                          >
                            <Box
                              sx={{
                                position: "relative",
                                width: canvasWidth,
                                height: canvasHeight,
                                overflow: "auto",
                              }}
                            >
                              <Box
                                sx={{
                                  position: "relative",
                                  width: canvasWidth * editorZoom,
                                  height: canvasHeight * editorZoom,
                                }}
                              >
                                <Box
                                  ref={logoPlacement.previewRef}
                                  onDrop={logoPlacement.handleDrop}
                                  onDragOver={logoPlacement.handleDragOver}
                                  sx={{
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    width: canvasWidth,
                                    height: canvasHeight,
                                    transform: `scale(${editorZoom})`,
                                    transformOrigin: "top left",
                                    overflow: "hidden",
                                    userSelect: "none",
                                  }}
                                >
                                  <img
                                    src={previewImageUrl}
                                    alt="PSD Preview"
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "contain",
                                      pointerEvents: "none",
                                      opacity: loadingPlacementPreview ? 0.7 : 1,
                                    }}
                                    draggable={false}
                                  />
                                  {logoPlacement.logoUrl && (
                                    <>
                                      {logoPlacement.printAreaBounds ? (
                                        <Box
                                          sx={{
                                            position: "absolute",
                                            left: logoPlacement.printAreaBounds.left,
                                            top: logoPlacement.printAreaBounds.top,
                                            width: logoPlacement.printAreaBounds.width,
                                            height: logoPlacement.printAreaBounds.height,
                                            overflow: "hidden",
                                            zIndex: 9,
                                            ...printAreaSx,
                                          }}
                                        >
                                          <Box
                                            ref={logoPlacement.logoRef}
                                            onMouseDown={logoPlacement.handleDragStart}
                                            sx={{
                                              position: "absolute",
                                              left:
                                                logoPlacement.logoTransform.x -
                                                logoPlacement.printAreaBounds.left,
                                              top:
                                                logoPlacement.logoTransform.y -
                                                logoPlacement.printAreaBounds.top,
                                              width: logoPlacement.logoTransform.width,
                                              height: logoPlacement.logoTransform.height,
                                              transform: `rotate(${logoPlacement.logoTransform.rotation}deg)`,
                                              transformOrigin: "center center",
                                              zIndex: 10,
                                              userSelect: "none",
                                              ...logoSelectionSx(logoPlacement.isDragging),
                                            }}
                                          >
                                            <img
                                              src={logoPlacement.logoUrl}
                                              alt="Logo"
                                              style={{
                                                width: "100%",
                                                height: "100%",
                                                objectFit: "contain",
                                                pointerEvents: "none",
                                                userSelect: "none",
                                              }}
                                              draggable={false}
                                            />
                                            <Box
                                              onMouseDown={logoPlacement.handleResizeStart}
                                              sx={resizeHandleSx}
                                            />
                                          </Box>
                                        </Box>
                                      ) : (
                                        <Box
                                          ref={logoPlacement.logoRef}
                                          onMouseDown={logoPlacement.handleDragStart}
                                          sx={{
                                            position: "absolute",
                                            left: logoPlacement.logoTransform.x,
                                            top: logoPlacement.logoTransform.y,
                                            width: logoPlacement.logoTransform.width,
                                            height: logoPlacement.logoTransform.height,
                                            transform: `rotate(${logoPlacement.logoTransform.rotation}deg)`,
                                            transformOrigin: "center center",
                                            zIndex: 10,
                                            userSelect: "none",
                                            ...logoSelectionSx(logoPlacement.isDragging),
                                          }}
                                        >
                                          <img
                                            src={logoPlacement.logoUrl}
                                            alt="Logo"
                                            style={{
                                              width: "100%",
                                              height: "100%",
                                              objectFit: "contain",
                                              pointerEvents: "none",
                                              userSelect: "none",
                                            }}
                                            draggable={false}
                                          />
                                          <Box
                                            onMouseDown={logoPlacement.handleResizeStart}
                                            sx={resizeHandleSx}
                                          />
                                        </Box>
                                      )}
                                    </>
                                  )}
                                </Box>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </Box>

                    {/* Unified bottom toolbar */}
                    <Box
                      sx={{
                        borderTop: "1px solid",
                        borderColor: EDITOR_UI.border,
                        bgcolor: "common.white",
                        px: { xs: 2, md: 3 },
                        py: 1.5,
                        display: "flex",
                        alignItems: "center",
                        gap: { xs: 1, md: 2 },
                        flexShrink: 0,
                        flexWrap: { xs: "wrap", lg: "nowrap" },
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ flex: { lg: 1 }, minWidth: 0 }}
                      >
                        <IconButton
                          size="small"
                          aria-label="Zoom out"
                          onClick={() => setEditorZoom((z) => Math.max(1, Number((z - 0.1).toFixed(2))))}
                          sx={editorIconBtnSx}
                        >
                          <ZoomOutOutlinedIcon fontSize="small" />
                        </IconButton>
                        <Slider
                          size="small"
                          min={1}
                          max={4}
                          step={0.1}
                          value={editorZoom}
                          onChange={(_, value) => setEditorZoom(value as number)}
                          aria-label="Canvas zoom"
                          sx={{
                            width: { xs: 72, sm: 88 },
                            color: EDITOR_UI.primary,
                            "& .MuiSlider-thumb": { width: 14, height: 14 },
                          }}
                        />
                        <IconButton
                          size="small"
                          aria-label="Zoom in"
                          onClick={() => setEditorZoom((z) => Math.min(4, Number((z + 0.1).toFixed(2))))}
                          sx={editorIconBtnSx}
                        >
                          <ZoomInOutlinedIcon fontSize="small" />
                        </IconButton>
                        <CustomTypography variant="body2" sx={{ fontSize: 13, fontWeight: 600, color: "text.secondary", minWidth: 40 }}>
                          {Math.round(editorZoom * 100)}%
                        </CustomTypography>
                        <Tooltip title="Fit to view" arrow>
                          <IconButton
                            size="small"
                            aria-label="Fit to view"
                            onClick={() => setEditorZoom(1)}
                            sx={{ ...editorIconBtnSx, display: { xs: "none", sm: "inline-flex" } }}
                          >
                            <FitScreenOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>

                      <Stack direction="row" alignItems="center" spacing={2} sx={{ ml: { lg: "auto" } }}>
                        <AppButton
                          label="Reset"
                          variant="secondary"
                          onClick={() => logoPlacement.handleLogoUrl(null)}
                          disabled={!logoPlacement.logoUrl || generatingMockup}
                          customStyles={{
                            minWidth: { xs: 88, md: 110 },
                            borderRadius: 10,
                            border: `1px solid ${EDITOR_UI.border}`,
                            minHeight: 40,
                          }}
                        />
                        <AppButton
                          label={generatingMockup ? "Generating..." : "Generate mockup"}
                          variant="primary"
                          onClick={handleGenerateMockup}
                          disabled={
                            !skuSelected || !logoPlacement.logoUrl || generatingMockup
                          }
                          customStyles={{
                            minWidth: { xs: 140, md: 180 },
                            borderRadius: 10,
                            minHeight: 42,
                            background: `linear-gradient(135deg, ${EDITOR_UI.primary} 0%, ${EDITOR_UI.primaryLight} 100%)`,
                            boxShadow: "0 4px 16px rgba(79, 70, 229, 0.35)",
                          }}
                        />
                      </Stack>
                    </Box>
                  </Box>
                ) : null}
              </Box>
            )}
          </>
        </Box>
      )}

      {tabs.showUploadContent && (
        <MockupManualUploadTab
          productId={product?.id}
          editMockupId={editMockupId}
          editMode={editMode}
          embedded={embedded}
          isCompactUploadDialog={tabs.isCompactUploadDialog}
          isBusy={isBusy}
          generatingMockup={generatingMockup}
          updatingMockupId={updatingMockupId}
          manualUploadFiles={tabs.manualUploadFiles}
          onFilesChange={(files) => tabs.setManualUploadFiles(files)}
          onRemovePreview={removeManualUploadPreview}
          skuSelected={skuSelected}
          onSave={() => void handleManualUploadSave()}
          onClose={onClose}
        />
      )}

      {!editMode && tabs.activeTab === "exports" && (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <MockupExportsTab
            preferredSize={preferredSize}
            savedMockups={savedMockups}
            mockupOutputs={mockupOutputs}
            mockupReferenceId={mockupReferenceId}
            deletingGeneratedMockupId={deletingGeneratedMockupId}
            onCopyUrl={handleCopyMockupUrl}
            onDeleteMockup={onDeleteMockup}
          />
        </Box>
      )}
    </>
  );

  const busyOverlay = isBusy ? (
    <Backdrop
      open
      sx={{
        position: "absolute",
        zIndex: (theme) => theme.zIndex.modal + 1,
        color: "#fff",
        backgroundColor: "rgba(0, 0, 0, 0.45)",
        borderRadius: "inherit",
      }}
    >
      <CircularProgress color="inherit" />
    </Backdrop>
  ) : null;

  if (embedded) {
    if (!open) return null;
    return (
      <Box sx={{ position: "relative", width: "100%", ...contentSx }}>
        {modalBody}
      </Box>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (isBusy) return;
        onClose();
      }}
      maxWidth={tabs.isCompactUploadDialog ? "md" : "lg"}
      fullWidth
      PaperProps={{
        sx: {
          position: "relative",
          maxWidth: tabs.isCompactUploadDialog ? 720 : 1120,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          ...(tabs.isCompactUploadDialog
            ? {}
            : {
                height: {
                  xs: "min(92vh, 100%)",
                  md: `min(92vh, ${GENERATOR_MODAL_MIN_HEIGHT}px)`,
                },
                maxHeight: "92vh",
                minHeight: { xs: 520, md: Math.min(GENERATOR_MODAL_MIN_HEIGHT, 720) },
              }),
        },
      }}
    >
      <DialogTitle
        sx={{
          flexShrink: 0,
          pr: 6,
          pb: tabs.isCompactUploadDialog ? 1 : undefined,
          ...(tabs.isCompactUploadDialog && {
            fontSize: "1rem",
            fontWeight: 600,
            lineHeight: 1.4,
          }),
        }}
      >
        {tabs.isCompactUploadDialog
          ? `Upload Mockups${product?.label ? ` - ${product.label}` : ""}`
          : `Mockup Generator${product?.label ? ` - ${product.label}` : ""}`}
        <IconButton
          aria-label="Close"
          onClick={onClose}
          disabled={isBusy}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          ...contentSx,
          // Keep DialogContent in the flex height chain so the Assets panel can scroll.
          "&.MuiDialogContent-root": {
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
          },
        }}
      >
        {modalBody}
      </DialogContent>
      {busyOverlay}
    </Dialog>
  );
};

export default observer(MockupGeneratorModal);
